import type { ICommandBarItemProps } from '@fluentui/react';
import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { TFunction } from 'i18next';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { processStartTemplate } from 'models/copilot/actions/aiActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import {
  CopilotChatInputUsageProps,
  CopilotPanelContextDefinition,
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelRuntime,
  CopilotPanelRuntimeView,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { CopilotAnnualPlanContent } from 'models/copilot/Processes/AnnualPlan/ProcessAnnualPlanContent';
import { TenantProcessStateAnnualPlan } from 'models/copilot/Processes/AnnualPlan/tenantProcessStateAnnualPlan';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import { apiCopilotExecuteProcessAnnualPlan } from 'services/Api/copilotService';
import { AutomationDraftingProcess } from '../AutomationDrafting/automationDraftingProcess';
import { getCopilotProcessWelcomeMessage } from '../copilotProcessMessages';
import { getSceneHelpBinding } from '../Generic/genericSceneBindings';
import { CopilotAnnualPlanPanelState } from './copilotAnnualPlanPanelState';

export class CopilotAnnualPlanPanelContextDefinition extends CopilotPanelContextDefinition {
  constructor() {
    super();
    this.context = CopilotPanelContext.AnnualPlan;
    this.name = 'AnnualPlan';
    this.supportsChat = true;
    this.requiresActiveAction = true;
  }

  getBindings(content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    // the help question comes first, before the process starts
    const bindings: AIActionBinding[] = [
      getSceneHelpBinding(),
      createAIActionBinding(processStartTemplate, { processId: TenantProcess.AnnualPlan }),
    ];
    // Draft automation for the plan as a whole: a check can be drafted for any of its activities
    if (content instanceof CopilotAnnualPlanContent && content.canDraftAutomation) {
      bindings.push(createAIActionBinding(processStartTemplate, { processId: TenantProcess.AutomationDrafting }));
    }

    return bindings;
  }

  async getAnswer(runtime: CopilotPanelRuntime, _t: TFunction<string[]>): Promise<void> {
    const { action } = runtime;
    if (AutomationDraftingProcess.isRunning(runtime)) {
      await AutomationDraftingProcess.execute(runtime, this.getEntityRef(runtime.content));

      return;
    }

    if (action?.processId !== TenantProcess.AnnualPlan) return;
    const state = this.getState(runtime);
    if (!state) return;

    runtime.setIsLoading(true);
    const answer = await apiCopilotExecuteProcessAnnualPlan(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      state.tasks,
      runtime.connectionId,
      runtime.accessToken,
      runtime.appContext.globalDataCache,
    );
    if (!answer) return;

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
    if (answer.tasks && answer.tasks.length > 0) {
      if (action.status === TenantProcessStatus.Completed) {
        runtime.resultBus.publishAnnualPlanTasks(answer.tasks);
      } else {
        state.setTasks(answer.tasks);
        state.setTasksDisplay(answer.tasks);
        runtime.setPreviewContent(CopilotPreviewContent.Usage);
      }
    }
    if (answer.controls && answer.controls.length > 0) {
      state.setControls(answer.controls);
    }
  }

  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (!this.isAvailableForContent(content)) return undefined;

    return { entityType: EntityTypes.NotSet, entityId: 0 };
  }

  getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    if (action.processId !== TenantProcess.AnnualPlan && !AutomationDraftingProcess.isDraftingAction(action)) {
      return undefined;
    }

    return getCopilotProcessWelcomeMessage(action, t);
  }

  getEntitySelectionQuestion(selectedEntityIds: number[], t: TFunction<string[]>): string {
    return t('copilot:Processes.AnnualPlan.Selection', { count: selectedEntityIds.length });
  }

  getCreatePreviewText(t: TFunction<string[]>): string | undefined {
    return t('copilot:Actions.AnnualPlan.Create');
  }

  // the drafting process allows typing in its refinement stage only; the annual plan process whenever it runs
  canStartChat(actions: CopilotAction[], runtime?: CopilotPanelRuntimeView): boolean {
    if (runtime && AutomationDraftingProcess.isRunning(runtime)) return AutomationDraftingProcess.canStartChat(runtime);

    return super.canStartChat(actions, runtime);
  }

  getChatInputUsageProps(runtime: CopilotPanelRuntime): CopilotChatInputUsageProps {
    return { ...super.getChatInputUsageProps(runtime), placeholder: AutomationDraftingProcess.getChatInputPlaceholder(runtime) };
  }

  getPreviewCommands(runtime: CopilotPanelRuntime, t: TFunction<string[]>): ICommandBarItemProps[] {
    return AutomationDraftingProcess.getPreviewCommands(runtime, t);
  }

  async onAdaptiveCardSubmit(
    runtime: CopilotPanelRuntime,
    block: CopilotConversationContentBlock,
    payload: AdaptiveSubmit<JsonObject>,
    t: TFunction<string[]>,
  ): Promise<boolean> {
    return AutomationDraftingProcess.onAdaptiveCardSubmit(runtime, block, payload, t);
  }

  applyProcessState(runtime: CopilotPanelRuntime, state: CopilotPanelProcessState): CopilotPreviewContent | undefined {
    const draftingPreview = AutomationDraftingProcess.applyProcessState(runtime, state);
    if (state.data instanceof TenantProcessStateAnnualPlan === false) {
      return draftingPreview ?? super.applyProcessState(runtime, state);
    }

    const panelState = this.getState(runtime);
    if (!(state.data instanceof TenantProcessStateAnnualPlan) || !panelState) {
      return super.applyProcessState(runtime, state);
    }

    panelState.setTasks(state.data.tasks);
    panelState.setTasksDisplay(state.data.tasks);
    panelState.setControls(state.data.controls);
    runtime.setConversation(state.conversation);

    return state.data.tasks.length > 0 ? CopilotPreviewContent.Usage : undefined;
  }

  clearPreview(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    state?.setTasks([]);
    state?.setTasksDisplay([]);
    AutomationDraftingProcess.clearPreview(runtime);
  }

  saveDisplayState(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    if (!state) return;

    state.setTasks(state.tasksDisplay);
  }

  discardDisplayState(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    if (!state) return;

    state.setTasksDisplay(state.tasks);
  }

  private getState(runtime: CopilotPanelRuntime): CopilotAnnualPlanPanelState | undefined {
    return runtime.getUsageState<CopilotAnnualPlanPanelState>(this.context);
  }
}
